// Almacenamiento offline de operaciones pendientes (App Campo, módulo
// Aplicaciones). IndexedDB puro, sin librerías: el volumen de datos y la
// forma de las consultas (por auth_user_id) son simples y no justifican
// agregar una dependencia nueva.
//
// IMPORTANTE — aislamiento entre usuarios: cada registro guarda su propio
// `auth_user_id`. Todas las lecturas están acotadas a un usuario (índice
// `by_auth_user_id`) y `clearUserData` borra únicamente los registros de
// ese usuario — nunca toda la base — para que, en un dispositivo compartido,
// cerrar sesión de un usuario no destruya la cola pendiente de otro que
// también haya usado ese teléfono.
//
// localStorage NO se usa aquí para datos operativos (solo para el flag de
// "ahora no" del prompt de instalación, en otro módulo).

const DB_NAME = 'familygrowers-campo-offline';
const DB_VERSION = 4;
const STORE = 'pending_aplicaciones';

// Store separado (no mezclado con `pending_aplicaciones`) para cachear el
// catálogo de productos_aplicacion y que el selector de productos siga
// funcionando sin conexión. Un único registro con key fija: no es una cola,
// es "la última copia descargada del catálogo".
const STORE_CATALOGO = 'catalogo_productos_aplicacion';
const CATALOGO_KEY = 'productos_aplicacion';

// Store separado para cachear los productores ASIGNADOS al encargado
// (selector de Productor en Aplicaciones/Fertilizantes). Particionado por
// auth_user_id (keyPath directo): cada usuario tiene su propio registro,
// así en un dispositivo compartido cerrar sesión de uno no expone la cache
// de otro (ver clearUserOfflineData). No se mezcla con
// catalogo_productos_aplicacion (ese catálogo es global, este es por
// usuario) ni con pending_aplicaciones.
//
// Reemplaza a 'catalogo_huertos_usuario' de la fase de huertos descartada
// (ver DB_VERSION=4: el upgrade elimina ese store si existía).
const STORE_PRODUCTORES = 'catalogo_productores_usuario';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB no disponible en este navegador.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'client_operation_id' });
        store.createIndex('by_auth_user_id', 'auth_user_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_CATALOGO)) {
        db.createObjectStore(STORE_CATALOGO, { keyPath: 'key' });
      }
      // Modelo de huertos descartado (ver fase de corrección): se elimina
      // el store de la implementación anterior si el navegador ya lo creó.
      if (db.objectStoreNames.contains('catalogo_huertos_usuario')) {
        db.deleteObjectStore('catalogo_huertos_usuario');
      }
      if (!db.objectStoreNames.contains(STORE_PRODUCTORES)) {
        db.createObjectStore(STORE_PRODUCTORES, { keyPath: 'auth_user_id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function promisifyRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function addPendingAplicacion(record) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).put(record);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listPendingAplicacionesByUser(authUserId) {
  if (!authUserId) return [];
  const db = await openDb();
  const tx = db.transaction(STORE, 'readonly');
  const index = tx.objectStore(STORE).index('by_auth_user_id');
  const results = await promisifyRequest(index.getAll(IDBKeyRange.only(authUserId)));
  return (results || []).sort((a, b) => (a.created_at_local < b.created_at_local ? 1 : -1));
}

export async function updatePendingAplicacionStatus(clientOperationId, changes) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  const existing = await promisifyRequest(store.get(clientOperationId));
  if (existing) {
    store.put({ ...existing, ...changes });
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function removePendingAplicacion(clientOperationId) {
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(clientOperationId);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Catálogo de productos: reemplaza la copia cacheada completa por la última
// lista de productos activos obtenida de Supabase (ver productosAplicacion.js).
export async function saveProductosCache(productos) {
  const db = await openDb();
  const tx = db.transaction(STORE_CATALOGO, 'readwrite');
  tx.objectStore(STORE_CATALOGO).put({ key: CATALOGO_KEY, productos, cachedAt: new Date().toISOString() });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Devuelve la última lista cacheada de productos, o null si nunca se guardó
// (p. ej. primer uso offline, sin conexión previa).
export async function getProductosCache() {
  const db = await openDb();
  const tx = db.transaction(STORE_CATALOGO, 'readonly');
  const record = await promisifyRequest(tx.objectStore(STORE_CATALOGO).get(CATALOGO_KEY));
  return record?.productos ?? null;
}

// Productores asignados: reemplaza la copia cacheada de ESTE usuario por la
// última lista obtenida de Supabase (ver services/productoresEncargado.js).
export async function saveProductoresCache(authUserId, productores) {
  if (!authUserId) return;
  const db = await openDb();
  const tx = db.transaction(STORE_PRODUCTORES, 'readwrite');
  tx.objectStore(STORE_PRODUCTORES).put({ auth_user_id: authUserId, productores, cachedAt: new Date().toISOString() });
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Devuelve la última lista cacheada de productores asignados a este
// usuario, o null si nunca se cacheó para él.
export async function getProductoresCache(authUserId) {
  if (!authUserId) return null;
  const db = await openDb();
  const tx = db.transaction(STORE_PRODUCTORES, 'readonly');
  const record = await promisifyRequest(tx.objectStore(STORE_PRODUCTORES).get(authUserId));
  return record?.productores ?? null;
}

// Se llama al cerrar sesión: borra solo los registros del usuario que se va,
// nunca la base completa. Incluye la cache de productores asignados de ese
// usuario (STORE_PRODUCTORES), para que un usuario B que inicie sesión
// después en el mismo dispositivo nunca vea la cache de un usuario A.
export async function clearUserOfflineData(authUserId) {
  if (!authUserId) return;
  const db = await openDb();
  const tx = db.transaction([STORE, STORE_PRODUCTORES], 'readwrite');
  const index = tx.objectStore(STORE).index('by_auth_user_id');
  const cursorRequest = index.openCursor(IDBKeyRange.only(authUserId));
  tx.objectStore(STORE_PRODUCTORES).delete(authUserId);

  await new Promise((resolve, reject) => {
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    cursorRequest.onerror = () => reject(cursorRequest.error);
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
